/**
 * @file RedirectsTab.tsx
 * @description Вкладка «Переадресации» раздела «Сайт → SEO» (phase19.16,
 * партия Р3в, v0.19.64; SB131, SB132; uiux-r3 §3.5): пояснение, поиск по
 * адресу, фильтр «Все · Авто · Ручные» со счётчиками, полоса «Осталось N из
 * 1000» с 900-го ручного правила, список и окна «Добавить/Изменить»,
 * «Проверить», подтверждение удаления.
 *
 * Кнопка «+ Добавить переадресацию» — главное действие страницы (в шапке;
 * до 1024 — в общей нижней полосе каркаса), поэтому окно добавления
 * открывает страница (`creating`). Без права настроек — только просмотр и
 * «Проверить», с подписью кто меняет.
 */

import { useState } from "react";

import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { FilterChips, FilterToggleChips } from "../../ui/FilterChips";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { SearchInput } from "../../ui/SearchInput";
import { useConfirm } from "../../ui/use-confirm";
import { RedirectDialog } from "./RedirectDialog";
import { RedirectProbeDialog } from "./RedirectProbeDialog";
import { RedirectsList } from "./RedirectsList";
import type { RedirectsApi } from "../../../hooks/site-seo/useRedirects";
import { siteRedirects, type SiteRedirectFilter, type SiteRedirectItem } from "../../../lib/api-site-redirects";
import { redirectLimitNote } from "../../../lib/site-seo/redirect-view";
import { seoErrorText } from "../../../lib/site-seo/seo-errors";

interface Props {
  api: RedirectsApi;
  canEdit: boolean;
  /** Окно «Добавить» открыто кнопкой страницы. */
  creating: boolean;
  onCreateClose: () => void;
}

export function RedirectsTab({ api, canEdit, creating, onCreateClose }: Props) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<SiteRedirectItem | null>(null);
  const [probe, setProbe] = useState<string | null>(null);
  const limit = redirectLimitNote(api.summary);
  const s = api.summary;

  async function remove(item: SiteRedirectItem) {
    const ok = await confirm.confirm({
      title: `Удалить переадресацию ${item.fromDisplay}?`,
      description: "Старый адрес начнёт отвечать «Страница не найдена» в течение минуты. Поисковики со временем уберут его из выдачи.",
      action: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      await siteRedirects.remove(item.id);
      notify.success(`Переадресация ${item.fromDisplay} удалена.`);
      await api.reload();
    } catch (err) {
      notify.error(seoErrorText(err, "Не удалось удалить переадресацию."));
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="type-body text-on-surface-variant">
        Со старого адреса покупатель и поисковик попадают на новый (код 301). Правило начинает действовать в течение минуты.
      </p>
      {canEdit ? null : <p className="type-caption text-on-surface-variant">Переадресации меняет владелец магазина или сотрудник с правом настроек магазина.</p>}
      {limit ? <NoticeStrip tone={limit.full ? "error" : "warning"}>{limit.text}</NoticeStrip> : null}
      <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-center">
        <SearchInput value={api.query} onChange={api.setQuery} placeholder="Найти адрес…" ariaLabel="Найти переадресацию по адресу" className="md:max-w-80" />
        <FilterChips<SiteRedirectFilter>
          aria-label="Какие переадресации показать"
          value={api.filter}
          onChange={api.setFilter}
          items={[
            { value: "all", label: "Все", count: s?.total },
            { value: "auto", label: "Авто", count: s?.auto },
            { value: "manual", label: "Ручные", count: s?.manual },
          ]}
        />
        {s && (s.inactive > 0 || api.inactiveOnly) ? (
          <FilterToggleChips
            aria-label="Не действующие переадресации"
            items={[{ key: "inactive", label: "Не действуют", count: s.inactive, pressed: api.inactiveOnly, onToggle: () => api.setInactiveOnly(!api.inactiveOnly) }]}
          />
        ) : null}
      </div>
      <RedirectsList api={api} canEdit={canEdit} onEdit={setEditing} onProbe={setProbe} onRemove={(item) => void remove(item)} />
      <RedirectDialog
        open={creating || editing !== null}
        editing={editing}
        onClose={() => {
          setEditing(null);
          onCreateClose();
        }}
        onSaved={() => void api.reload()}
        onEditExisting={(item) => {
          onCreateClose();
          setEditing(item);
        }}
      />
      <RedirectProbeDialog path={probe} onClose={() => setProbe(null)} />
      <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
    </div>
  );
}
